import { useEffect, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { Suggestion } from '@/lib/askFollowUps';

interface Props {
  /** Starters (no answer yet) show open; follow-ups sit behind "Ask a follow-up". */
  mode: 'starters' | 'follow-ups';
  suggestions: Suggestion[];
  onPick: (s: Suggestion) => void;
  /** Hidden while an answer streams so the answer owns the space. */
  hidden?: boolean;
  /** Changes when a new answer lands, closing the tray again. */
  resetKey?: string | number;
  className?: string;
}

/** The one Ask Cannafax suggestion surface: never permanent content above the composer. */
export function SuggestionTray({ mode, suggestions, onPick, hidden, resetKey, className }: Props) {
  const [open, setOpen] = useState(false);
  useEffect(() => { setOpen(false); }, [resetKey]);
  if (hidden || suggestions.length === 0) return null;

  const chips = (
    <div className="no-scrollbar flex gap-1.5 overflow-x-auto pb-1 sm:flex-wrap sm:overflow-visible">
      {suggestions.map((s) => (
        <Button key={s.text} type="button" variant="chip" size="chip"
          onClick={() => { setOpen(false); onPick(s); }}
          className="shrink-0 text-left sm:shrink">
          {s.text}
        </Button>
      ))}
    </div>
  );

  if (mode === 'starters') return <div className={cn('pb-2', className)}>{chips}</div>;

  return (
    <div className={cn('pb-1.5', className)}>
      {open && <div className="mb-1">{chips}</div>}
      <Button type="button" variant="ghost" size="sm" aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="h-7 gap-1 px-2 text-xs text-muted-foreground">
        {open ? 'Hide suggestions' : 'Ask a follow-up'}
        {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronUp className="h-3.5 w-3.5" />}
      </Button>
    </div>
  );
}
